/** Original seated Grand Elder Leo. The scene owns the encounter and completion;
 * this owner seats the original source and keeps reveal/light/shadow lifetimes. */
import { Scene } from "@babylonjs/core/scene";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { scopeCanopyReadingLight } from "../../levels/art/CanopyReadingLight";

const ELDER_LEO_MODEL = "/assets/models/npcs/elder_leo_canopy.glb";
export interface ElderLeoPresentation {
  seatY: number;
  yaw?: number;
  shadowGenerator: ShadowGenerator;
  readingLight?: PointLight;
}

export class ElderLeo {
  readonly mesh: Mesh;
  readonly modelReady: Promise<void>;
  private readonly material: StandardMaterial;
  private readonly glowLight: PointLight;
  private readonly ownsLight: boolean;
  private model: InstantiatedModel | null = null;
  private mount: TransformNode | null = null;
  private casters: AbstractMesh[] = [];
  private lightTargets: AbstractMesh[] = [];
  private releaseLighting:(()=>void)|null=null;
  private disposed = false;
  private readonly sceneDispose;

  constructor(private readonly scene: Scene, position: Vector3, private readonly presentation?: ElderLeoPresentation) {
    this.mesh=MeshBuilder.CreateCapsule("grand_elder_leo",{height:1.6,radius:.4,tessellation:16,subdivisions:6},scene);
    this.mesh.position.copyFrom(position);
    this.material=new StandardMaterial("grand_elder_leo_mat",scene);
    this.material.diffuseColor=Color3.FromHexString("#c6aa78");this.material.specularColor=Color3.Black();
    this.mesh.material=this.material;
    this.mesh.bakeTransformIntoVertices(Matrix.Translation(0,this.seatY-(position.y-.8),0));
    this.ownsLight=!presentation?.readingLight;
    this.glowLight=presentation?.readingLight??new PointLight("grand_elder_leo_glow",position.add(new Vector3(-.8,.8,.4)),scene);
    if(this.ownsLight){this.glowLight.diffuse.set(.85,.65,.36);this.glowLight.specular=Color3.Black();this.glowLight.intensity=.35;this.glowLight.range=3;}
    this.mesh.onDisposeObservable.addOnce(()=>this.dispose());
    this.sceneDispose=scene.onDisposeObservable.addOnce(()=>this.dispose());
    this.syncPresentation();this.modelReady=this.load();
  }
  private get seatY():number{return this.presentation?.seatY??this.mesh.position.y-.8;}
  private async load():Promise<void>{
    let candidate:InstantiatedModel|null=null;
    try{
      candidate=await getAssetManager().instantiateModel(this.scene,ELDER_LEO_MODEL,undefined,{doNotInstantiate:true});
      if(this.disposed||this.mesh.isDisposed()||this.scene.isDisposed){candidate.dispose();return;}
      const geometry=candidate.meshes.filter(m=>m.getTotalVertices()>0);
      if(!candidate.rootNodes.length||!geometry.length||geometry.some(m=>!m.material||m.material.alpha!==1||m.material.needAlphaBlendingForMesh(m)||m.material.needAlphaTestingForMesh(m)))throw Error("Incomplete opaque Leo source");
      const min=Math.min(...geometry.map(m=>{m.computeWorldMatrix(true);return m.getBoundingInfo().boundingBox.minimumWorld.y;}));
      this.mount=new TransformNode("leo_seatedArt",this.scene);this.mount.parent=this.mesh;
      this.mount.rotation.y=this.presentation?.yaw??0;
      this.mount.position.y=this.seatY-this.mesh.position.y-min;
      for(const root of candidate.rootNodes)root.parent=this.mount;
      for(const mesh of geometry){
        mesh.isPickable=false;mesh.receiveShadows=true;
        const material=mesh.material as {maxSimultaneousLights?:number}|null;
        if(typeof material?.maxSimultaneousLights==="number")material.maxSimultaneousLights=4;
      }
      this.model=candidate;this.mesh.isVisible=false;this.syncPresentation();
    }catch(error){candidate?.dispose();if(!this.disposed&&!this.scene.isDisposed)console.warn("[ElderLeo] Keeping supported fallback:",error);}
  }
  /** Seated paws stay on their physical cushion. */
  update(_time:number,_dt:number):void{}
  setRevealed(revealed:boolean):void{
    if(this.disposed)return;this.mesh.setEnabled(revealed);if(this.ownsLight)this.glowLight.setEnabled(revealed);this.syncPresentation();
  }
  private syncPresentation():void{
    this.releaseLighting?.();this.releaseLighting=null;
    for(const mesh of this.casters)this.presentation?.shadowGenerator.removeShadowCaster(mesh,false);
    this.casters=[];
    const selected=this.mesh.isEnabled()?(this.model?.meshes??[this.mesh]).filter(m=>m.getTotalVertices()>0):[];
    for(const mesh of selected){mesh.receiveShadows=true;this.presentation?.shadowGenerator.addShadowCaster(mesh,false);this.casters.push(mesh);}
    const other=this.glowLight.includedOnlyMeshes.filter(m=>!this.lightTargets.includes(m));
    this.lightTargets=selected;this.glowLight.includedOnlyMeshes=[...other,...selected];
    if(selected.length)this.releaseLighting=scopeCanopyReadingLight(this.scene,this.glowLight,selected);
  }
  dispose():void{
    if(this.disposed)return;this.disposed=true;this.scene.onDisposeObservable.remove(this.sceneDispose);
    this.releaseLighting?.();this.releaseLighting=null;
    for(const mesh of this.casters)this.presentation?.shadowGenerator.removeShadowCaster(mesh,false);this.casters=[];
    this.glowLight.includedOnlyMeshes=this.glowLight.includedOnlyMeshes.filter(m=>!this.lightTargets.includes(m));this.lightTargets=[];
    if(this.ownsLight)this.glowLight.dispose();this.model?.dispose();this.model=null;this.mount?.dispose();this.mount=null;
    this.material.dispose();if(!this.mesh.isDisposed())this.mesh.dispose();
  }
}
